import useSWR, { SWRResponse } from 'swr'
import { FormEvent, useEffect, useState } from 'react'
import { Ipermission } from 'common-recruspin-codebase/types/permission'
import { Tabs, Tab, Box, Button } from '@mui/material'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocation, useNavigate } from 'react-router-dom'
import BoxWrapper from '../../components/BoxWrapper'
import Loading from '../../components/Loading'
import SuccessModal from '../../components/SuccessModal'
import FormErrorsModal from '../../components/FormErrorsModal'
import axiosInstance from '../../utils/axiosInstance'
import { PermissionSettings } from './PermissionSettings'
import { CustomTabPanel, a11yProps } from '../../components/CustomTabPanel'
import './style.scss'

export type Permissions = {
  reseller: Ipermission
  resellerCustomer: Ipermission
  directCustomer: Ipermission
}

const ACCOUNTS_TABS = [
  { id: 0, label: 'Reseller', path: '/reseller' },
  { id: 1, label: 'Reseller customer', path: '/reseller-customer' },
  { id: 2, label: 'Direct customer', path: '/direct-customer' },
] as const

const AccountsAndPermissions = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()

  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [errorsModalOpen, setErrorsModalOpen] = useState(false)
  const [formikErrors, setFormikErrors] = useState<string[]>([])
  const [mutationLoading, setMutationLoading] = useState(false)

  const [value, setValue] = useState(0)
  const {
    data: permissions,
    isLoading,
    error,
  }: SWRResponse = useSWR('/universalPermissions', axiosInstance)
  const data: Permissions = permissions?.data.permissions

  const [resellerPermissions, setResellerPermissions] = useState<Ipermission>(data?.reseller)
  const [resellerCustomerPermissions, setResellerCustomerPermissions] = useState<Ipermission>(
    data?.resellerCustomer
  )
  const [directCustomerPermissions, setDirectCustomerPermissions] = useState<Ipermission>(
    data?.directCustomer
  )

  useEffect(() => {
    if (data && !resellerPermissions) {
      setResellerPermissions(data.reseller)
      setResellerCustomerPermissions(data.resellerCustomer)
      setDirectCustomerPermissions(data.directCustomer)
    }
  }, [data, resellerPermissions])

  const saveSettings = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    delete resellerPermissions.id
    delete resellerCustomerPermissions.id
    delete directCustomerPermissions.id

    const newData = {
      reseller: resellerPermissions,
      resellerCustomer: resellerCustomerPermissions,
      directCustomer: directCustomerPermissions,
    }
    const isChanged = JSON.stringify(newData) !== JSON.stringify(data)
    if (!isChanged) return

    try {
      setMutationLoading(true)
      await axiosInstance.post('/universalPermissions', newData)
      setSuccessModalOpen(true)
    } catch (error) {
      setErrorsModalOpen(true)
      setFormikErrors([String(error)])
    } finally {
      setMutationLoading(false)
    }
  }

  const handleTabChange = (_e, v) => {
    const selectedTab = ACCOUNTS_TABS[v]
    navigate(`/permissions${selectedTab.path}`)
  }

  useEffect(() => {
    const tabPath = location.pathname.split('/permissions')[1] || ''
    const foundTab = ACCOUNTS_TABS.findIndex((tab) => tab.path === tabPath)
    if (foundTab >= 0) {
      setValue(foundTab)
    } else {
      setValue(0)
    }
  }, [location.pathname])

  if (isLoading || mutationLoading) return <Loading />
  if (error) return <div>{error.message}</div>
  if (!resellerPermissions) return <div>no data</div>

  return (
    <div className="AccountsAndPermissions">
      <form onSubmit={saveSettings}>
        <BoxWrapper>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={value} onChange={handleTabChange} aria-label="basic tabs example">
              <Tab label="Reseller" {...a11yProps(0)} />
              <Tab label="Reseller Customer" {...a11yProps(1)} />
              <Tab label="Direct Customer" {...a11yProps(2)} />
              <div className="buttons">
                <Button className="save" variant="outlined" type="submit">
                  <FormattedMessage id="cancel" />
                </Button>
                <Button className="save" variant="contained" type="submit">
                  <FormattedMessage id="Save" />
                </Button>
              </div>
            </Tabs>
          </Box>
          <CustomTabPanel value={value} index={0}>
            <PermissionSettings data={resellerPermissions} setData={setResellerPermissions} />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <PermissionSettings
              data={resellerCustomerPermissions}
              setData={setResellerCustomerPermissions}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <PermissionSettings
              data={directCustomerPermissions}
              setData={setDirectCustomerPermissions}
            />
          </CustomTabPanel>
        </BoxWrapper>
      </form>
      <SuccessModal
        successModalOpen={successModalOpen}
        handleClose={() => setSuccessModalOpen(false)}
        setSuccessModalOpen={setSuccessModalOpen}
        message={intl.formatMessage({ id: 'updated-successfully' })}
      />
      <FormErrorsModal
        errorsModalOpen={errorsModalOpen}
        formikErrors={formikErrors}
        setErrorsModalOpen={setErrorsModalOpen}
      />
    </div>
  )
}

export default AccountsAndPermissions
